import type { ComponentProps } from 'react';

import { cn } from '../../lib/cn';

export type SwitchProps = Omit<ComponentProps<'input'>, 'type' | 'onChange' | 'checked'> & {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

/**
 * Feature-flag toggle of Figma CMS-16: green when on, grey when off.
 *
 * A **native** `<input type="checkbox" role="switch">` under the paint (QA iteration 2 UX-209).
 * It used to be Radix's `Switch`, which renders a `<button role="switch">` plus a hidden
 * `<input>` for form submission — that bubble input carries no accessible name, so an audit of
 * `/settings` reported five unlabelled inputs, `entitlements.enforce` (the flag that opens the
 * whole catalog) among them. A real input also takes a real `<label for>`, which is what the
 * settings and device screens now give it, and keeps `data-state` for the existing tests.
 */
export function Switch({ className, checked, onCheckedChange, ...props }: SwitchProps) {
  const state = checked ? 'checked' : 'unchecked';
  return (
    <span
      data-state={state}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-line bg-line transition-colors',
        'has-[:checked]:border-ok has-[:checked]:bg-ok has-[:disabled]:opacity-50',
        className,
      )}
    >
      <input
        {...props}
        type="checkbox"
        role="switch"
        data-state={state}
        checked={checked ?? false}
        onChange={(event) => onCheckedChange?.(event.target.checked)}
        className="peer absolute inset-0 size-full cursor-pointer appearance-none rounded-full outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink disabled:cursor-not-allowed"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none block size-5 translate-x-0.5 rounded-full bg-panel transition-transform peer-checked:translate-x-[22px]"
      />
    </span>
  );
}
